import { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { authService, marketplaceListingService, transactionService, watchlistService, reviewService } from '../services/api';

interface User {
  id: number;
  displayName: string;
  email: string;
  points: number;
  profilePicture?: string;
  lineUserId?: string;
}

interface Listing {
  id: number;
  status: string;
  price: number;
  listingType: 'WTS' | 'WTB';
  condition: string;
  createdAt: string;
  isBundle?: boolean;
  bundleName?: string;
  customCardName?: string;
  imageUrls?: string;
  updatedAt?: string;
  closingPrice?: number;
  card?: {
    id: number;
    name: string;
    imageUrl?: string;
  };
}

const renderStars = (rating: number) => (
  <div className="flex items-center gap-0.5">
    {[1, 2, 3, 4, 5].map((star) => (
      <span key={star} className={star <= rating ? 'text-amber-500' : 'text-gray-300'}>
        ★
      </span>
    ))}
  </div>
);

interface Transaction {
  id: number;
  status: string;
  agreedPrice: number;
  createdAt: string;
  updatedAt: string;
  listing?: {
    id: number;
    card?: {
      id: number;
      name: string;
      imageUrl?: string;
    };
  };
  buyer?: {
    id: number;
    displayName: string;
    profilePicture?: string;
  };
  seller?: {
    id: number;
    displayName: string;
    profilePicture?: string;
  };
}

interface WatchlistItem {
  id: number;
  cardId: number;
  card?: {
    id: number;
    name: string;
    imageUrl?: string;
    marketplaceListings?: {
      id: number;
      price: number;
      listingType: 'WTS' | 'WTB';
    }[];
  };
}

interface ActivityItem {
  id: string;
  type: 'listing' | 'purchase' | 'sale' | 'transaction_update';
  title: string;
  description: string;
  timestamp: string;
  link?: string;
}



const DashboardPage = () => {
  const { t } = useTranslation();
  const [user, setUser] = useState<User | null>(null);
  const [myListings, setMyListings] = useState<Listing[]>([]);
  const [myPurchases, setMyPurchases] = useState<Transaction[]>([]);
  const [mySales, setMySales] = useState<Transaction[]>([]);

  const [watchlist, setWatchlist] = useState<WatchlistItem[]>([]);
  const [recentActivity, setRecentActivity] = useState<ActivityItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [userRatings, setUserRatings] = useState<Record<number, { averageRating: number; totalReviews: number }>>({});

  // LINE Linking State
  // LINE Linking State
  const [linkingLine, setLinkingLine] = useState(false);

  const statusLabels: Record<string, { label: string; color: string }> = {
    ACTIVE: { label: t('dashboard.status.available'), color: 'bg-green-100 text-green-800' },
    AVAILABLE: { label: t('dashboard.status.available'), color: 'bg-green-100 text-green-800' },
    RESERVED: { label: t('dashboard.status.reserved'), color: 'bg-yellow-100 text-yellow-800' },
    SOLD: { label: t('dashboard.status.sold'), color: 'bg-gray-100 text-gray-800' },
    CANCELLED: { label: t('dashboard.status.cancelled'), color: 'bg-red-100 text-red-800' },
    PENDING_PAYMENT: { label: t('dashboard.status.pendingPayment'), color: 'bg-yellow-100 text-yellow-800' },
    PAYMENT_SUBMITTED: { label: t('dashboard.status.paymentSubmitted'), color: 'bg-blue-100 text-blue-800' },
    PAYMENT_CONFIRMED: { label: t('dashboard.status.paymentConfirmed'), color: 'bg-green-100 text-green-800' },
    SHIPPED: { label: t('dashboard.status.shipped'), color: 'bg-purple-100 text-purple-800' },
    COMPLETED: { label: t('dashboard.status.completed'), color: 'bg-gray-100 text-gray-800' },
  };



  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch user profile
        const userProfile = await authService.getProfile();
        setUser(userProfile);

        // Fetch all data in parallel
        const [
          listingsResponse,
          purchasesResponse,
          salesResponse,
          watchlistResponse,
        ] = await Promise.all([
          marketplaceListingService.getUserListings(String(userProfile.id), { limit: 50, includeInactive: true }),
          transactionService.getUserPurchases({ limit: 50 }),
          transactionService.getUserSales({ limit: 50 }),
          watchlistService.getWatchlist().catch(() => ({ data: { data: [] } })),
        ]);

        const allListings = listingsResponse.data.data || [];
        const purchases = purchasesResponse.data.data || [];
        const sales = salesResponse.data.data || [];
        const watchlistItems = watchlistResponse.data.data || [];

        // Filter active listings for the "My Active Listings" section
        const activeOnly = allListings.filter((l: Listing) => ['ACTIVE', 'AVAILABLE'].includes(l.status));
        setMyListings(activeOnly);

        // Process COMPLETED listings to add to history
        const completedListings = allListings.filter((l: Listing) => l.status === 'COMPLETED');

        // Convert completed WTS listings to "Sales"
        const salesFromListings = completedListings
          .filter((l: Listing) => l.listingType === 'WTS')
          .map((l: Listing) => ({
            id: -l.id, // Use negative ID to avoid collision with real transactions
            seller: userProfile, // Me
            buyer: { id: 0, displayName: t('dashboard.dateDisplay') ? 'Community' : 'Community' }, // Placeholder for unknown buyer
            status: 'COMPLETED',
            agreedPrice: l.closingPrice || l.price,
            createdAt: l.createdAt,
            updatedAt: l.updatedAt || l.createdAt,
            listing: l
          }));

        // Convert completed WTB listings to "Purchases"
        const purchasesFromListings = completedListings
          .filter((l: Listing) => l.listingType === 'WTB')
          .map((l: Listing) => ({
            id: -l.id, // Use negative ID
            seller: { id: 0, displayName: t('dashboard.dateDisplay') ? 'Community' : 'Community' }, // Placeholder for unknown seller
            buyer: userProfile, // Me
            status: 'COMPLETED',
            agreedPrice: l.closingPrice || l.price,
            createdAt: l.createdAt,
            updatedAt: l.updatedAt || l.createdAt,
            listing: l
          }));

        // Merge and sort
        const allPurchases = [...purchases, ...purchasesFromListings].sort((a, b) =>
          new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
        );
        const allSales = [...sales, ...salesFromListings].sort((a, b) =>
          new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
        );

        setMyPurchases(allPurchases);
        setMySales(allSales);
        setWatchlist(watchlistItems);

        const userIds = new Set<number>();
        purchases.forEach((tx: Transaction) => {
          if (tx.buyer?.id) userIds.add(tx.buyer.id);
          if (tx.seller?.id) userIds.add(tx.seller.id);
        });
        sales.forEach((tx: Transaction) => {
          if (tx.buyer?.id) userIds.add(tx.buyer.id);
          if (tx.seller?.id) userIds.add(tx.seller.id);
        });


        if (userIds.size > 0) {
          const ratingsEntries = await Promise.all(
            Array.from(userIds).map(async (id) => {
              try {
                const res = await reviewService.getUserReviews(String(id), { page: 1, limit: 1 });
                const summary = res.data.data.summary;
                return [id, { averageRating: summary?.averageRating || 0, totalReviews: summary?.totalReviews || 0 }] as const;
              } catch (err) {
                console.error('Failed to fetch rating for user', id, err);
                return [id, { averageRating: 0, totalReviews: 0 }] as const;
              }
            })
          );
          const ratingsMap: Record<number, { averageRating: number; totalReviews: number }> = {};
          ratingsEntries.forEach(([id, rating]) => {
            ratingsMap[id] = rating;
          });
          setUserRatings(ratingsMap);
        }

        // Generate recent activity from transactions
        const activity: ActivityItem[] = [];

        // Add listings as activity
        activeOnly.slice(0, 5).forEach((listing: Listing) => {
          activity.push({
            id: `listing-${listing.id}`,
            type: 'listing',
            title: t('dashboard.listedForSale'),
            description: listing.card?.name || 'Card',
            timestamp: listing.createdAt,
            link: `/marketplace/listing/${listing.id}`,
          });
        });

        // Add purchases as activity (real + manual)
        allPurchases.slice(0, 5).forEach((tx: Transaction) => {
          activity.push({
            id: `purchase-${tx.id}`,
            type: 'purchase',
            title: `${t('dashboard.purchase')} ${t('dashboard.status.completed')}`,
            description: tx.listing?.card?.name || 'Card',
            timestamp: tx.updatedAt || tx.createdAt,
            link: tx.id < 0 ? `/marketplace/listing/${tx.listing!.id}` : `/marketplace/transaction/${tx.id}`,
          });
        });

        // Add sales as activity (real + manual)
        allSales.slice(0, 5).forEach((tx: Transaction) => {
          activity.push({
            id: `sale-${tx.id}`,
            type: 'sale',
            title: `${t('dashboard.sale')} ${t('dashboard.status.completed')}`,
            description: tx.listing?.card?.name || 'Card',
            timestamp: tx.updatedAt || tx.createdAt,
            link: tx.id < 0 ? `/marketplace/listing/${tx.listing!.id}` : `/marketplace/transaction/${tx.id}`,
          });
        });

        // Sort by timestamp and take most recent 10
        activity.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
        setRecentActivity(activity.slice(0, 10));
      } catch (error) {
        console.error('Failed to fetch data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = date.getTime() - now.getTime(); // positive when in the future
    const absMs = Math.abs(diffMs);
    const diffMins = Math.floor(absMs / 60000);
    const diffHours = Math.floor(absMs / 3600000);
    const diffDays = Math.floor(absMs / 86400000);

    if (diffMs > 0) {
      if (diffMins < 1) return t('dashboard.time.in', { time: '<1m' });
      if (diffMins < 60) return t('dashboard.time.in', { time: `${diffMins}m` });
      if (diffHours < 24) return t('dashboard.time.in', { time: `${diffHours}h` });
      if (diffDays < 7) return t('dashboard.time.in', { time: `${diffDays}d` });
      return date.toLocaleDateString('th-TH');
    }

    if (diffMins < 1) return t('dashboard.time.justNow');
    if (diffMins < 60) return t('dashboard.time.ago', { time: `${diffMins}m` });
    if (diffHours < 24) return t('dashboard.time.ago', { time: `${diffHours}h` });
    if (diffDays < 7) return t('dashboard.time.ago', { time: `${diffDays}d` });
    return date.toLocaleDateString('th-TH');
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('th-TH').format(price);
  };



  const handleLineLogin = async () => {
    try {
      const redirectUri = window.location.origin + window.location.pathname;
      const url = await authService.getLineLoginUrl(redirectUri);
      window.location.href = url;
    } catch (error) {
      console.error('Failed to get LINE login URL:', error);
      alert('Failed to initiate LINE login. Please try again.');
    }
  };

  // Check for LINE login code
  const linkingRef = useRef(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const error = params.get('error');

    if (error) {
      alert('LINE Login failed. Please try again.');
      // Clean URL
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (code && !linkingLine && !linkingRef.current) {
      linkingRef.current = true; // Mark as started
      const linkAccount = async () => {
        try {
          setLinkingLine(true);
          // Use current URL without query params as redirect URI (must match what was used to generate login URL)
          // Note: The backend uses LINE_LOGIN_CALLBACK_URL env var. 
          // If that env var is set to http://localhost:3000/dashboard, then we should pass that here.
          // Ideally, we pass window.location.origin + window.location.pathname
          const redirectUri = window.location.origin + window.location.pathname;

          await authService.linkLineAccount(code, redirectUri);

          // Refresh user profile
          const userProfile = await authService.getProfile();
          setUser(userProfile);

          // alert('LINE account linked successfully!'); // Removed as per user request
        } catch (error) {
          console.error('Failed to link LINE account:', error);
          alert('Failed to link LINE account. Please try again.');
        } finally {
          setLinkingLine(false);
          // Clean URL
          window.history.replaceState({}, document.title, window.location.pathname);
        }
      };

      linkAccount();
    }
  }, []);



  // Filter active listings
  const activeListings = myListings.filter((listing) => ['AVAILABLE', 'ACTIVE'].includes(listing.status));



  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-48 mb-4"></div>
          <div className="grid md:grid-cols-4 gap-4 mb-8">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-24 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2">{t('dashboard.title')}</h1>
        <p className="text-gray-600">
          {t('dashboard.welcome', { name: user?.displayName || 'User' })}
        </p>
      </div>





      {/* Quick Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-2xl font-bold text-blue-600">
            {activeListings.filter(l => l.listingType === 'WTS').length}
          </div>
          <div className="text-xs md:text-sm text-gray-600">{t('dashboard.wtsListings')}</div>
        </div>

        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-2xl font-bold text-orange-500">
            {activeListings.filter(l => l.listingType === 'WTB').length}
          </div>
          <div className="text-xs md:text-sm text-gray-600">{t('dashboard.wtbListings')}</div>
        </div>

        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-2xl font-bold text-gray-700">
            {watchlist.length}
          </div>
          <div className="text-xs md:text-sm text-gray-600">{t('dashboard.watchlist')}</div>
        </div>

        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-2xl font-bold text-yellow-600">{user?.points || 0}</div>
          <div className="text-xs md:text-sm text-gray-600">{t('dashboard.points')}</div>
        </div>



        <Link to="/support" className="bg-blue-50 rounded-lg p-4 flex flex-col items-center justify-center hover:bg-blue-100 transition">
          <span className="text-2xl font-bold text-blue-600">?</span>
          <span className="text-xs md:text-sm text-blue-800 font-medium">{t('support.contactSupport', 'Contact Support')}</span>
        </Link>
      </div>

      {/* Active Transactions Alert */}


      <div className="grid lg:grid-cols-3 gap-8">
        {/* Left Column */}
        <div className="lg:col-span-2 space-y-8">
          {/* My Active Listings */}
          <div className="bg-white rounded-lg shadow">
            <div className="p-4 border-b flex justify-between items-center">
              <h2 className="text-xl font-bold">{t('dashboard.myActiveListingsTitle')}</h2>
              <div className="flex gap-3">
                <Link to="/marketplace/sell?type=WTB" className="text-blue-600 hover:underline text-sm font-semibold">
                  {t('dashboard.createNewBuy')}
                </Link>
                <Link to="/marketplace/sell?type=WTS" className="text-blue-600 hover:underline text-sm font-semibold">
                  {t('dashboard.createNewSell')}
                </Link>
              </div>
            </div>
            <div className="p-4">
              {activeListings.length === 0 ? (
                <div className="text-center py-4">
                  <p className="text-gray-500 mb-2">{t('dashboard.noActiveListings')}</p>
                  <div className="flex gap-2 justify-center">
                    <Link
                      to="/marketplace/sell?type=WTB"
                      className="inline-block px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                    >
                      {t('dashboard.createNewBuy')}
                    </Link>
                    <Link
                      to="/marketplace/sell?type=WTS"
                      className="inline-block px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 transition-colors"
                    >
                      {t('dashboard.createNewSell')}
                    </Link>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  {activeListings.slice(0, 5).map((listing) => {
                    const cardName = listing.isBundle
                      ? (listing.bundleName || t('marketplaceListing.bundle', 'Bundle'))
                      : (listing.card?.name || listing.customCardName || 'Card');

                    let imageUrl = listing.card?.imageUrl;
                    if (!imageUrl && listing.imageUrls) {
                      try {
                        const images = JSON.parse(listing.imageUrls);
                        if (Array.isArray(images) && images.length > 0) {
                          imageUrl = images[0];
                        }
                      } catch (e) {
                        // ignore parse error
                      }
                    }

                    return (
                      <Link
                        key={listing.id}
                        to={`/marketplace/listing/${listing.id}`}
                        className="flex items-center gap-4 p-3 border rounded-lg hover:bg-gray-50 transition-colors"
                      >
                        <div className="w-12 h-12 bg-gray-200 rounded overflow-hidden flex-shrink-0">
                          {imageUrl ? (
                            <img
                              src={imageUrl}
                              alt={cardName}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-gray-400">
                              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                              </svg>
                            </div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-0.5">
                            <span className={`text-[10px] px-1.5 py-0.5 rounded font-bold ${listing.listingType === 'WTB' ? 'bg-orange-100 text-orange-800' : 'bg-blue-100 text-blue-800'
                              }`}>
                              {listing.listingType}
                            </span>
                            <div className="font-medium truncate">{cardName}</div>
                          </div>

                        </div>
                        <div className="text-right">
                          <div className="font-medium">{formatPrice(listing.price)} THB</div>
                          <div className="text-xs text-gray-500">{formatDate(listing.createdAt)}</div>
                        </div>
                      </Link>
                    );
                  })}
                  {activeListings.length > 5 && (
                    <div className="text-center pt-2">
                      <Link to="/my-listings" className="text-blue-600 hover:underline text-sm">
                        {t('dashboard.viewAllListings', { count: activeListings.length })}
                      </Link>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* My Purchases */}
          <div className="bg-white rounded-lg shadow">
            <div className="p-4 border-b flex justify-between items-center">
              <h2 className="text-xl font-bold">{t('dashboard.myPurchasesTitle')}</h2>
              <span className="text-sm text-gray-500">{myPurchases.length} {t('dashboard.total')}</span>
            </div>
            <div className="p-4">
              {myPurchases.length === 0 ? (
                <div className="text-center py-4">
                  <p className="text-gray-500 mb-2">{t('dashboard.noPurchases')}</p>
                  <Link
                    to="/marketplace"
                    className="inline-block px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                  >
                    {t('dashboard.browseMarketplace')}
                  </Link>
                </div>
              ) : (
                <div className="space-y-3">
                  {myPurchases.slice(0, 5).map((tx) => {
                    const cardName = tx.listing?.card?.name || 'Card';
                    const statusInfo = statusLabels[tx.status] || { label: tx.status, color: 'bg-gray-100 text-gray-800' };

                    return (
                      <Link
                        key={tx.id}
                        to={`/marketplace/transaction/${tx.id}`}
                        className="flex items-center gap-4 p-3 border rounded-lg hover:bg-gray-50 transition-colors"
                      >
                        <div className="w-12 h-12 bg-gray-200 rounded overflow-hidden flex-shrink-0">
                          {tx.listing?.card?.imageUrl ? (
                            <img
                              src={tx.listing.card.imageUrl}
                              alt={cardName}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-gray-400">
                              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                              </svg>
                            </div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium truncate">{cardName}</div>
                          <div className="text-sm text-gray-500">
                            {t('dashboard.from')}{' '}
                            {tx.seller?.id ? (
                              <Link to={`/users/${tx.seller.id}/reviews`} className="text-blue-700 font-semibold hover:underline">
                                {tx.seller.displayName}
                              </Link>
                            ) : (
                              tx.seller?.displayName || t('dashboard.unknown')
                            )}
                          </div>
                          {tx.seller && tx.seller.id > 0 && userRatings[tx.seller.id] && (
                            <div className="mt-1 flex items-center gap-1 text-xs text-gray-600">
                              {renderStars(Math.round(userRatings[tx.seller.id].averageRating))}
                              <span className="font-semibold text-gray-800">
                                {userRatings[tx.seller.id].averageRating.toFixed(1)}
                              </span>
                              <span className="text-gray-500">({userRatings[tx.seller.id].totalReviews})</span>
                            </div>
                          )}
                        </div>
                        <div className="text-right">
                          <div className="font-medium">{formatPrice(tx.agreedPrice)} THB</div>
                          <span className={`inline-block px-2 py-0.5 rounded text-xs ${statusInfo.color}`}>
                            {statusInfo.label}
                          </span>
                        </div>
                      </Link>
                    );
                  })}
                  {myPurchases.length > 5 && (
                    <div className="text-center pt-2">
                      <Link to="/my-transactions?tab=purchases" className="text-blue-600 hover:underline text-sm">
                        {t('dashboard.viewAllPurchases', { count: myPurchases.length })}
                      </Link>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* My Sales */}
          <div className="bg-white rounded-lg shadow">
            <div className="p-4 border-b flex justify-between items-center">
              <h2 className="text-xl font-bold">{t('dashboard.mySalesTitle')}</h2>
              <span className="text-sm text-gray-500">{mySales.length} {t('dashboard.total')}</span>
            </div>
            <div className="p-4">
              {mySales.length === 0 ? (
                <p className="text-gray-500 text-center py-4">{t('dashboard.noSales')}</p>
              ) : (
                <div className="space-y-3">
                  {mySales.slice(0, 5).map((tx) => {
                    const cardName = tx.listing?.card?.name || 'Card';
                    const statusInfo = statusLabels[tx.status] || { label: tx.status, color: 'bg-gray-100 text-gray-800' };

                    return (
                      <Link
                        key={tx.id}
                        to={`/marketplace/transaction/${tx.id}`}
                        className="flex items-center gap-4 p-3 border rounded-lg hover:bg-gray-50 transition-colors"
                      >
                        <div className="w-12 h-12 bg-gray-200 rounded overflow-hidden flex-shrink-0">
                          {tx.listing?.card?.imageUrl ? (
                            <img
                              src={tx.listing.card.imageUrl}
                              alt={cardName}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-gray-400">
                              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                              </svg>
                            </div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium truncate">{cardName}</div>
                          <div className="text-sm text-gray-500">
                            {t('dashboard.to')}{' '}
                            {tx.buyer?.id ? (
                              <Link to={`/users/${tx.buyer.id}/reviews`} className="text-blue-700 font-semibold hover:underline">
                                {tx.buyer.displayName}
                              </Link>
                            ) : (
                              tx.buyer?.displayName || t('dashboard.unknown')
                            )}
                          </div>
                          {tx.buyer && tx.buyer.id > 0 && userRatings[tx.buyer.id] && (
                            <div className="mt-1 flex items-center gap-1 text-xs text-gray-600">
                              {renderStars(Math.round(userRatings[tx.buyer.id].averageRating))}
                              <span className="font-semibold text-gray-800">
                                {userRatings[tx.buyer.id].averageRating.toFixed(1)}
                              </span>
                              <span className="text-gray-500">({userRatings[tx.buyer.id].totalReviews})</span>
                            </div>
                          )}
                        </div>
                        <div className="text-right">
                          <div className="font-medium">{formatPrice(tx.agreedPrice)} THB</div>
                          <span className={`inline-block px-2 py-0.5 rounded text-xs ${statusInfo.color}`}>
                            {statusInfo.label}
                          </span>
                        </div>
                      </Link>
                    );
                  })}
                  {mySales.length > 5 && (
                    <div className="text-center pt-2">
                      <Link to="/my-transactions?tab=sales" className="text-blue-600 hover:underline text-sm">
                        {t('dashboard.viewAllSales', { count: mySales.length })}
                      </Link>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>




        </div>

        {/* Right Column */}
        <div className="space-y-8">
          {/* Recent Activity */}
          <div className="bg-white rounded-lg shadow">
            <div className="p-4 border-b">
              <h2 className="text-xl font-bold">{t('dashboard.recentActivityTitle')}</h2>
            </div>
            <div className="p-4">
              {recentActivity.length === 0 ? (
                <p className="text-gray-500 text-center py-4">{t('dashboard.noRecentActivity')}</p>
              ) : (
                <div className="space-y-3">
                  {recentActivity.map((activity) => {
                    const activityIcons: Record<string, string> = {
                      listing: 'M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10',
                      purchase: 'M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z',
                      sale: 'M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
                      transaction_update: 'M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15',
                    };

                    return (
                      <Link
                        key={activity.id}
                        to={activity.link || '#'}
                        className="flex items-start gap-3 p-2 rounded hover:bg-gray-50 transition-colors"
                      >
                        <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                          <svg className="w-4 h-4 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={activityIcons[activity.type] || activityIcons.listing} />
                          </svg>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium">{activity.title}</div>
                          <div className="text-xs text-gray-500 truncate">{activity.description}</div>
                        </div>
                        <div className="text-xs text-gray-400">
                          {formatDate(activity.timestamp)}
                        </div>
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* My Watchlist */}
          <div className="bg-white rounded-lg shadow">
            <div className="p-4 border-b flex justify-between items-center">
              <h2 className="text-xl font-bold">{t('dashboard.myWatchlistTitle')}</h2>
              <span className="text-sm text-gray-500">{watchlist.length} {t('home.cards')}</span>
            </div>
            <div className="p-4">
              {watchlist.length === 0 ? (
                <div className="text-center py-4">
                  <p className="text-gray-500 mb-2">{t('dashboard.noWatchlist')}</p>
                  <Link
                    to="/browse-cards"
                    className="text-blue-600 hover:underline text-sm"
                  >
                    {t('dashboard.browseCards')}
                  </Link>
                </div>
              ) : (
                <div className="space-y-3">
                  {watchlist.slice(0, 5).map((item) => {
                    const cardName = item.card?.name || 'Card';
                    const listings = item.card?.marketplaceListings || [];

                    const sellListings = listings.filter((l: any) => l.listingType === 'WTS' || !l.listingType);
                    const buyListings = listings.filter((l: any) => l.listingType === 'WTB');

                    const bestSellPrice = sellListings.length > 0
                      ? Math.min(...sellListings.map((l: any) => Number(l.price)))
                      : null;

                    const bestBuyPrice = buyListings.length > 0
                      ? Math.max(...buyListings.map((l: any) => Number(l.price)))
                      : null;

                    return (
                      <Link
                        key={item.id}
                        to={`/cards/${item.cardId}`}
                        className="flex items-center gap-3 p-2 rounded hover:bg-gray-50 transition-colors"
                      >
                        <div className="w-12 h-12 bg-gray-200 rounded overflow-hidden flex-shrink-0">
                          {item.card?.imageUrl ? (
                            <img
                              src={item.card.imageUrl}
                              alt={cardName}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-gray-400">
                              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                              </svg>
                            </div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium truncate">{cardName}</div>
                          <div className="flex gap-3 mt-1">
                            {bestSellPrice !== null ? (
                              <div className="text-xs">
                                <span className="text-gray-500">{t('dashboard.sell')}:</span> <span className="font-semibold text-blue-600">฿{formatPrice(bestSellPrice)}</span>
                              </div>
                            ) : (
                              <div className="text-xs text-gray-400">{t('dashboard.noSellers')}</div>
                            )}
                            {bestBuyPrice !== null ? (
                              <div className="text-xs">
                                <span className="text-gray-500">{t('dashboard.buy')}:</span> <span className="font-semibold text-green-600">฿{formatPrice(bestBuyPrice)}</span>
                              </div>
                            ) : (
                              <div className="text-xs text-gray-400">{t('dashboard.noBuyers')}</div>
                            )}
                          </div>
                        </div>
                      </Link>
                    );
                  })}
                  {watchlist.length > 5 && (
                    <div className="text-center pt-2">
                      <Link to="/browse-cards" className="text-blue-600 hover:underline text-sm">
                        {t('dashboard.viewAllWatchlist', { count: watchlist.length })}
                      </Link>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
          {/* Quick Actions */}
          <div className="bg-white rounded-lg shadow">
            <div className="p-4 border-b">
              <h2 className="text-xl font-bold">{t('dashboard.quickActionsTitle')}</h2>
            </div>
            <div className="p-4 space-y-2">
              <div className="grid grid-cols-2 gap-2">
                <Link
                  to="/marketplace/sell?type=WTB"
                  className="block w-full px-4 py-2 bg-blue-600 text-white text-center rounded hover:bg-blue-700 transition-colors"
                >
                  {t('dashboard.createNewBuy')}
                </Link>
                <Link
                  to="/marketplace/sell?type=WTS"
                  className="block w-full px-4 py-2 bg-green-600 text-white text-center rounded hover:bg-green-700 transition-colors"
                >
                  {t('dashboard.createNewSell')}
                </Link>
              </div>
              <Link
                to="/marketplace"
                className="block w-full px-4 py-2 bg-gray-100 text-gray-700 text-center rounded hover:bg-gray-200 transition-colors"
              >
                {t('dashboard.browseMarketplace')}
              </Link>
              <Link
                to="/browse-cards"
                className="block w-full px-4 py-2 bg-gray-100 text-gray-700 text-center rounded hover:bg-gray-200 transition-colors"
              >
                {t('dashboard.browseCards')}
              </Link>
              <button
                onClick={handleLineLogin}
                disabled={linkingLine || !!user?.lineUserId}
                className={`block w-full px-4 py-2 text-white text-center rounded transition-colors flex items-center justify-center gap-2 disabled:opacity-80 ${user?.lineUserId ? 'bg-green-600 hover:bg-green-700' : 'bg-[#00B900] hover:bg-[#009900]'
                  }`}
              >
                {user?.lineUserId ? (
                  <>
                    <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current">
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
                    </svg>
                    {t('dashboard.lineLinked')}
                  </>
                ) : (
                  <>
                    <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current">
                      <path d="M20.3 10c0-4.6-4.5-8.3-10-8.3S.3 5.4.3 10c0 4.1 3.6 7.5 8.4 8.2.3.1.8.3.9 1 .1.6 0 1.3-.1 1.6-.1.4-.6 1.6-.6 1.6s-.2.9.5.5c.7-.4 3.9-2.2 5.3-3.8 2.9-1.6 5.6-4.7 5.6-9.1z" />
                    </svg>
                    {linkingLine ? t('dashboard.connecting') : t('dashboard.linkLineAccount')}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;
